import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getArtists, getImageUrl } from '../../utils/api';
import { artists as mockArtists } from '../../data/mockData';
import './FeaturedArtists.css';

const FeaturedArtists = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    fetchArtists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchArtists = async () => {
    try {
      const data = await getArtists();
      if (data && data.length > 0) {
        const featured = data.filter(a => a.featured);
        setArtists(featured.length > 0 ? featured : data);
      } else {
        setArtists(mockArtists);
      }
    } catch (error) {
      console.log('Using mock data for artists');
      setArtists(mockArtists);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 320; // Card width + gap
      const newScrollLeft = sliderRef.current.scrollLeft + (direction === 'next' ? scrollAmount : -scrollAmount);
      sliderRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section id="artists" className="featured-artists">
        <div className="container">
          <div className="section-header">
            <span className="section-label">The Roster</span>
            <h2>Artists with identity</h2>
            <p>AMR is built around artists with a clear sound, a real point of view, and records that leave a mark.</p>
          </div>
          <div className="loading-spinner">Loading artists...</div>
        </div>
      </section>
    );
  }

  if (artists.length === 0) {
    return null;
  }

  return (
    <section id="artists" className="featured-artists">
      <div className="container">
        <div className="section-header">
          <span className="section-label">The Roster</span>
          <h2>Artists with identity</h2>
          <p>AMR is built around artists with a clear sound, a real point of view, and records that leave a mark. Meet the people shaping the first chapter.</p>
        </div>

        <div className="slider-wrapper fade-in">
          <button 
            className="slider-btn prev"
            onClick={() => scroll('prev')}
            aria-label="Previous artists"
            data-testid="artist-prev-btn"
          >
            <ChevronLeft size={28} />
          </button>

          <div className="artists-slider" ref={sliderRef}>
            {artists.map((artist) => (
              <div key={artist.id} className="artist-slide" data-testid={`artist-card-${artist.id}`}>
                <div className="artist-image-container">
                  <img 
                    src={getImageUrl(artist.image)} 
                    alt={artist.name}
                    className="artist-image"
                    loading="lazy"
                  />
                </div>
                <div className="artist-details">
                  <h3 className="artist-name">{artist.name}</h3>
                  {artist.slug && (
                    <Link to={`/artists/${artist.slug}`} className="read-more-link">
                      Read more
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button 
            className="slider-btn next"
            onClick={() => scroll('next')}
            aria-label="Next artists"
            data-testid="artist-next-btn"
          >
            <ChevronRight size={28} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedArtists;
